<script lang="ts">
  // Popover — a non-modal dialog floating under (or above) its trigger (wiki/feature-design-system.md).
  // The host owns `open`. Focus moves into the panel on open; Escape and the Close
  // control return it to the trigger region's `aria-controls={id}` element, while a
  // pointer outside closes without stealing focus from what it pressed.
  import { tick, type Snippet } from 'svelte';
  import { cubicOut } from 'svelte/easing';
  import type { TransitionConfig } from 'svelte/transition';
  import Button from './Button.svelte';
  import { X } from '$lib/ui/icons';

  // Without a header the title only names the panel; Escape, a press outside
  // and the trigger still close it.
  type Header = { header?: true; closeLabel: string } | { header: false; closeLabel?: never };

  type Props = Header & {
    id: string;
    open: boolean;
    title: string;
    onclose: () => void;
    /** `stretch` spans the trigger region's full width. */
    align?: 'start' | 'end' | 'stretch';
    side?: 'below' | 'above';
    trigger: Snippet;
    children: Snippet;
    'data-testid'?: string;
  };

  let {
    id,
    open,
    title,
    header = true,
    closeLabel,
    onclose,
    align = 'start',
    side = 'below',
    trigger,
    children,
    'data-testid': dataTestid,
  }: Props = $props();

  const titleId = $derived(`${id}-title`);
  let rootEl = $state<HTMLDivElement | undefined>(undefined);
  let panelEl = $state<HTMLDivElement | undefined>(undefined);

  // A panel above its trigger grows up out of it and folds back down into it;
  // one below appears in place.
  function grow(_panel: Element): TransitionConfig {
    if (side !== 'above' || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return { duration: 0 };
    return {
      duration: 180,
      easing: cubicOut,
      css: (t) => `opacity: ${t}; transform: scaleY(${0.6 + 0.4 * t});`,
    };
  }

  $effect(() => {
    if (!open) return;
    let returnFocus = true;
    void tick().then(() =>
      (panelEl?.querySelector<HTMLElement>('input, select, textarea') ?? panelEl?.querySelector('button'))?.focus(),
    );
    function onKey(e: KeyboardEvent): void {
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      onclose();
    }
    function onPointer(e: Event): void {
      if (rootEl && !rootEl.contains(e.target as Node)) {
        returnFocus = false;
        onclose();
      }
    }
    window.addEventListener('keydown', onKey, true);
    window.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('keydown', onKey, true);
      window.removeEventListener('pointerdown', onPointer, true);
      if (returnFocus) rootEl?.querySelector<HTMLElement>(`[aria-controls="${id}"]`)?.focus();
    };
  });
</script>

<div class="vd-popover" bind:this={rootEl}>
  {@render trigger()}
  {#if open}
    <div
      bind:this={panelEl}
      {id}
      class="vd-popover__panel vd-popover__panel--{align}"
      class:vd-popover__panel--above={side === 'above'}
      transition:grow
      role="dialog"
      aria-labelledby={header ? titleId : undefined}
      aria-label={header ? undefined : title}
      data-testid={dataTestid}
    >
      {#if header}
        <header class="vd-popover__head">
          <h2 id={titleId}>{title}</h2>
          <Button variant="ghost" icon={X} aria-label={closeLabel ?? title} onclick={onclose} data-testid="popover-close" />
        </header>
      {/if}
      {@render children()}
    </div>
  {/if}
</div>

<style lang="scss">
  .vd-popover {
    position: relative;
    min-width: 0;
  }

  .vd-popover__panel {
    position: absolute;
    top: calc(100% + 0.35rem);
    z-index: 50;
    box-sizing: border-box;
    width: max-content;
    max-width: calc(100vw - 2rem);
    padding: 0.75rem 1rem 1rem;
    background: var(--paper);
    border: 1px solid var(--line);
    border-radius: 10px;
    box-shadow: 0 8px 24px color-mix(in srgb, var(--ink) 18%, transparent);
  }

  .vd-popover__panel--above {
    top: auto;
    bottom: calc(100% + 0.35rem);
    transform-origin: bottom;
  }

  .vd-popover__panel--start {
    left: 0;
  }

  .vd-popover__panel--end {
    right: 0;
  }

  .vd-popover__panel--stretch {
    left: 0;
    right: 0;
    width: auto;
  }

  .vd-popover__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 1rem;
    margin-bottom: 0.5rem;

    h2 {
      margin: 0;
      font-size: var(--text-body);
    }
  }
</style>
